import { getTokenDefinition } from "../tokens/registry";
import { restoreRawTokens } from "./serialization";

/**
 * Anchor → link-token conversion. The platform's link engine only tracks `{url}[text]` link markup;
 * a literal `<a>` survives into Email untracked and is missing/raw in SMS (serialization contract §3).
 * These helpers are the single place anchors become canonical link tokens — used by `editorToHtml`,
 * `editorToMessage`, and the HTML field's serializer.
 */

/** Schemes/values that carry no trackable destination — such an anchor is unwrapped to its text. */
const NON_NAVIGABLE = /^\s*(javascript:|#|$)/i;

/** The registry's generic `{url}[text]` builder — the canonical encoder, kept in sync with the parser. */
const linkDefinition = getTokenDefinition("link");

/**
 * Build canonical `{url}[text]` link markup, with an optional trailing `<style>` suffix inside the
 * brackets (contract §3.2). Routes through the registry encoder rather than concatenating braces,
 * since URLs may themselves contain `{ }` or nested `{{…}}` tokens.
 */
export const buildLinkString = ({ url, text, style }: { url: string; text: string; style?: string }): string => {
  const display = style ? `${text}<${style}>` : text;
  return linkDefinition?.build({ url, text: display }) ?? `{${url}}[${display}]`;
};

/**
 * Inline tags whose presence inside an anchor maps onto a CSS declaration. TipTap nests formatting
 * marks *inside* the link mark (e.g. `<a><strong>hi</strong></a>`), so a link's styling is found by
 * descending into the anchor, not by inspecting its ancestors.
 */
const TAG_STYLE: Record<string, string> = {
  STRONG: "font-weight:bold",
  B: "font-weight:bold",
  EM: "font-style:italic",
  I: "font-style:italic",
  U: "text-decoration:underline",
};

/** Split a CSS `style` attribute into individual `prop:value` declarations, trimmed and non-empty. */
const declarationsOf = (style: string): string[] =>
  style.split(";").reduce<string[]>((declarations, raw) => {
    const declaration = raw.trim().replace(/\s*:\s*/, ":");
    return declaration ? [...declarations, declaration] : declarations;
  }, []);

/** Every element at or under `root` (root first, then descendants in document order). */
const elementsFrom = (root: Element): Element[] => [root, ...[...root.children].flatMap(elementsFrom)];

/**
 * The CSS declarations that style a link, in a deterministic order: the anchor's own inline style
 * first, then the contributions of each styled element nested within it (formatting tags and inline
 * `style` attributes). Duplicates are dropped (first occurrence wins) so the suffix is byte-stable.
 */
const collectLinkStyle = (anchor: HTMLAnchorElement): string => {
  const declarations = elementsFrom(anchor).flatMap((element) => {
    const tagStyle = element === anchor ? undefined : TAG_STYLE[element.tagName];
    return [...(tagStyle ? [tagStyle] : []), ...declarationsOf(element.getAttribute("style") ?? "")];
  });

  const seen = new Set<string>();
  return declarations.filter((declaration) => !seen.has(declaration) && seen.add(declaration)).join(";");
};

/**
 * The display text inside an anchor: token chip-spans collapsed back to their raw `{{…}}` / `{url}[text]`
 * strings (so a tokened label survives into the `[text]` slot verbatim), with all formatting markup
 * dropped — its styling has been lifted into the suffix by {@link collectLinkStyle}.
 */
const anchorText = (anchor: HTMLAnchorElement): string => {
  const container = anchor.ownerDocument.createElement("div");
  container.innerHTML = restoreRawTokens(anchor.innerHTML);
  return container.textContent ?? "";
};

/**
 * Convert a single anchor element to its canonical representation: `{href}[text<style?>]` for a
 * navigable href, or the bare `text` for a non-navigable one. `text` is the caller-provided display
 * text (with tokens already collapsed to raw); styling is read off the anchor and its descendants.
 * Shared by {@link anchorsToTokens} and the raw HTML field's serializer.
 */
export const anchorElementToMarkup = (anchor: HTMLAnchorElement, text: string): string => {
  const href = anchor.getAttribute("href") ?? "";
  if (NON_NAVIGABLE.test(href)) return text;
  return buildLinkString({ url: href, text, style: collectLinkStyle(anchor) || undefined });
};

/**
 * Replace every `<a>` in `html` with canonical link markup. A navigable href becomes
 * `{href}[text<style?>]`; an anchor with no usable destination (no href, `#`, empty, or a
 * `javascript:` scheme) is unwrapped to its text alone. Non-anchor markup is left untouched.
 *
 * Each replacement is staged behind a private sentinel and spliced into the serialized output
 * afterward: link markup carries literal `<`/`>` (the `<style>` suffix) and `{ }` `[ ]` that the DOM
 * serializer would HTML-escape if inserted as a text node, so we keep it out of the DOM entirely.
 */
export const anchorsToTokens = (html: string): string => {
  if (!html.includes("<a")) return html;

  const doc = new DOMParser().parseFromString(html, "text/html");
  const replacements: string[] = [];
  const sentinelFor = (value: string): string => `@@ANCHOR_LINK_${replacements.push(value) - 1}@@`;

  doc.querySelectorAll("a").forEach((anchor) => {
    const markup = anchorElementToMarkup(anchor, anchorText(anchor));
    anchor.replaceWith(doc.createTextNode(sentinelFor(markup)));
  });

  return doc.body.innerHTML.replace(
    /@@ANCHOR_LINK_(\d+)@@/g,
    (_match, index: string) => replacements[Number(index)] ?? ""
  );
};
